<template>
    <div :class="{ 'is-mobile': isMobile }">
        <CheckTicket/>
        <div
            :class="{
                'is-purchasing': isTicketSubmitting,
                'empty': isEmpty,
                'undo': undoSelections,
            }"
            class="betslip"
        >
            <transition name="animation__scale-up-ver-top-">
                <div
                    v-if="isTicketSubmitting"
                    class="betslip__disable-helper"
                >
                    <template v-if="isTicketSubmitting">
                        <Illustration
                            large
                            image="/static/img/illustrations/betslip__purchase-loading.svg"
                            class="betslip__purchase-loading-ring"
                        />
                        <div class="betslip__purchase-loading-copy">
                            {{ $t('Placing bet...') }}
                        </div>
                    </template>
                </div>
            </transition>
            <BetSlipHeader
                :hasTypes="false"
                @collapse="$emit('close')"
            />
            <Toolbar
                v-if="undoSelections || selectionsCount"
                :selectionsCount="selectionsCount"
                :undoSelections="undoSelections"
                :selectionNames="$t('numbers')"
                @clear="clear"
                @undoClear="undoClear"
            />
            <div class="betslip__static__top">
                <Errors
                    v-if="error"
                    :errors="[error]"
                    :areValidationErrors="true"
                    @fadeOut="clearError"
                />
                <Errors
                    v-if="submitErrors.length"
                    :errors="submitErrors"
                    @fadeOut="removeError"
                />
            </div>
            <div class="betslip-body" >
                <div class="betslip-body__side">
                    <transition name="animation-list__slide-in-left-">
                        <div
                            v-if="!selectionsCount"
                            class="empty-state__betslip"
                        >
                            <Illustration image="/static/img/illustrations/betslip__empty-state--lotto.svg"/>
                            <div class="empty-state__betslip--content">
                                {{ $t('Betslip is empty. Select events from offer and place your bet.') }}
                            </div>
                        </div>
                        <LottoBetSlip
                            v-if="selectionsCount"
                            :selected="selections"
                            :eventDescription="eventDescription"
                            :systems="systems"
                            @removeNumberClick="onRemoveNumber"
                            @systemClick="onSystemClick"
                        />
                    </transition>
                </div>
            </div>
            <div class="betslip-body__side betslip__static__bottom">
                <div class="betslip__details">
                    <Quota
                        :isSimple="false"
                        :totalNumberOfSelectedCombinations="payoutSummary.combinations"
                    />
                    <Stake
                        :stake="rawStake"
                        :isDisabled="!!ticketNegotiation"
                        @change="setStake"
                        @stakePick="setStake"
                    />
                    <Tax
                        v-if="taxPercentage"
                        :stake="rawStake"
                        :stakeAfterTax="stakeAfterTax"
                        :taxPercentage="taxPercentage"
                    />
                </div>
                <div class="betslip__static-content">
                    <PayoutSummary
                        :payout="payout"
                        :isOnline="isOnline"
                        :stakeAfterTax="stakeAfterTax"
                    />
                    <div class="betslip__actions">
                        <CustomButton
                            @click="onCreateClick"
                            class="betslip__button"
                            :class="purchaseButtonClass"
                            :isLoading="isTicketSubmitting"
                        >
                            {{ isOnline ? $t('bet now') : $t('prepare bet') | capitalize }}
                        </CustomButton>
                    </div>
                    <TicketNegotiation
                        v-if="ticketNegotiation"
                        :ticketNegotiation="ticketNegotiation"
                        :selections="[]"
                        @accept="acceptTicketNegotiation"
                        @decline="refuseTicketNegotiation"
                        @error="ticketNegotiationError"
                    />
                </div>
                <BetSlipFooter
                    :isLoggedIn="!!user"
                    :isOnline="isOnline"
                    @typeClick="setPurchaseType"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import { isEmpty as _isEmpty } from '@lodash';
    import { mapActions, mapGetters } from 'vuex';
    import BetSlipHeader from '@shared/betSlip/BetSlipHeader.vue';
    import PayoutSummary from '@countryModules/shared/betSlip/PayoutSummary.vue';
    import CheckTicket from '@app/modules/sportOffer/betSlip/components/CheckTicket.vue';
    import BetSlipFooter from '@app/modules/shared/betSlip/BetSlipFooter.vue';
    import Toolbar from '@shared/betSlip/Toolbar';
    import Tax from '@shared/betSlip/Tax';
    import TicketNegotiation from '@shared/betSlip/TicketNegotiation';
    import Stake from '@shared/betSlip/Stake.vue';
    import Quota from '@shared/betSlip/Quota';
    import Errors from '@shared/betSlip/Errors.vue';
    import { errorGuard } from '@app/utils';
    import { DEFAULT_USER_BALANCE } from '@store/modules/data/authentication/account';
    import LottoBetSlip from '@shared/lotto/betSlip/LottoBetSlip.vue';
    import Modals from '@store/modules/ui/shared/modal/modals';
    import { isMobile } from '@app/layout';


    export default {
        name: 'BetSlipContainer',
        created() {
            this.setDefaultStake();
        },
        computed: {
            ...mapGetters('data/country', {
                isLottoInMaintenance: 'isLottoInMaintenance',
            }),
            ...mapGetters('data/authentication', [
                'user',
                'userBalanceWithBonus',
            ]),
            ...mapGetters('ui/lotto/betSlip', [
                'isEmpty',
                'selections',
                'selectionsCount',
                'systems',
                'rawStake',
                'payout',
                'payoutSummary',
                'eventName',
                'numbersDrawn',
                'totalNumbers',
                'eventDate',
                'purchaseType',
                'isOnline',
                'error',
                'isSimple',
                'undoSelections',
                'stakeAfterTax',
                'taxPercentage'
            ]),
            ...mapGetters('data/tickets', [
                'isTicketSubmitting',
                'submitErrors',
                'ticketNegotiation',
            ]),
            eventDescription() {
                return {
                    eventName: this.eventName,
                    numbersDrawn: this.numbersDrawn,
                    totalNumbers: this.totalNumbers,
                    eventDate: this.eventDate
                };
            },
            purchaseButtonClass() {
                return ({ btn_not_enough_balance: (!this.hasEnoughMoneyForStake && this.isOnline) });
            },
            hasEnoughMoneyForStake() {
                if (this.userBalanceWithBonus === DEFAULT_USER_BALANCE || !this.rawStake) {
                    return true;
                }
                return this.rawStake <= this.userBalanceWithBonus;
            },
            isMobile() {
                return isMobile();
            },
        },
        methods: {
            ...mapActions('data/country', [
                'fetchConfig'
            ]),
            ...mapActions('ui/lotto/betSlip', [
                'toggleSystemSelected',
                'setStake',
                'clear',
                'setPurchaseType',
                'toggleSelection',
                'submitTicket',
                'clearError',
                'undoClear',
                'setDefaultStake',
            ]),
            ...mapActions('data/tickets', [
                'removeError',
                'acceptTicketNegotiation',
                'refuseTicketNegotiation',
                'ticketNegotiationError',
                'setTicketSubmitLoading',
            ]),
            ...mapActions('ui/modal', [
                'setModal',
            ]),
            onRemoveNumber(number) {
                this.toggleSelection(number);
            },
            onSystemClick(system) {
                this.toggleSystemSelected(system);
            },
            async onCreateClick() {
                await errorGuard({
                    action: async () => {
                        this.setTicketSubmitLoading(true);
                        const isInMaintenance = await this.isInMaintenance();

                        if (isInMaintenance) {
                            this.setModal({
                                code: Modals.maintenance.code,
                            });
                            this.setTicketSubmitLoading(false);
                            return;
                        }
                        await this.submitTicket();
                        if (_isEmpty(this.submitErrors) && _isEmpty(this.error)) {
                            this.$store.dispatch('ui/lotto/betSlip/clear');
                        }
                    },
                    onError: () => this.setModal({
                        code: Modals.ticketSubmitRetry.code,
                        data: {
                            betSlipNamespace: 'ui/lotto/betSlip',
                        },
                    }),
                });
                this.setTicketSubmitLoading(false);
            },
            async isInMaintenance() {
                await this.fetchConfig();
                return this.isLottoInMaintenance;
            }
        },
        components: {
            LottoBetSlip,
            Stake,
            BetSlipHeader,
            PayoutSummary,
            Errors,
            Tax,
            BetSlipFooter,
            CheckTicket,
            Toolbar,
            TicketNegotiation,
            Quota
        },
    };
</script>



// WEBPACK FOOTER //
// src/app/modules/lotto/lottoBetSlip/LottoBetSlipContainer.vue